import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { correspondentService } from '../../services/correspondent.service'
import { customFieldService } from '../../services/custom-field.service'
import { documentTypeService } from '../../services/document-type.service'
import { storagePathService } from '../../services/storage-path.service'
import { toastService } from '../../services/toast.service'
import { Correspondent, CustomField, DocumentType, StoragePath } from '../../types'
import { InputTags } from '../common/InputTags'
import { DocumentLinkInput } from '../common/DocumentLinkInput'
import { MonetaryInput } from '../common/MonetaryInput'
import './CreateDocumentDialog.scss'

interface CreateDocumentDialogProps {
  show: boolean
  onHide: () => void
  onCreate: (payload: {
    title?: string
    document_type: number
    correspondent?: number | null
    storage_path?: number | null
    tags?: number[]
    custom_fields?: Record<number, any>
    template_id?: number | null
  }) => Promise<void>
}

const CreateDocumentDialog: React.FC<CreateDocumentDialogProps> = ({ show, onHide, onCreate }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([])
  const [selectedDocumentTypeDetails, setSelectedDocumentTypeDetails] = useState<DocumentType | null>(null)
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [correspondents, setCorrespondents] = useState<Correspondent[]>([])
  const [storagePaths, setStoragePaths] = useState<StoragePath[]>([])
  const [title, setTitle] = useState('')
  const [documentTypeId, setDocumentTypeId] = useState<number | null>(null)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [correspondentId, setCorrespondentId] = useState<number | null>(null)
  const [storagePathId, setStoragePathId] = useState<number | null>(null)
  const [tags, setTags] = useState<number[]>([])
  const [customFieldValues, setCustomFieldValues] = useState<Record<number, any>>({})

  useEffect(() => {
    if (!show) return
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [dtResponse, cResponse, spResponse, cfResponse] = await Promise.all([
          documentTypeService.listAll(),
          correspondentService.listAll(),
          storagePathService.listAll(),
          customFieldService.listAll(),
        ])
        setDocumentTypes(dtResponse.results || [])
        setCorrespondents(cResponse.results || [])
        setStoragePaths(spResponse.results || [])
        setCustomFields(cfResponse.results || [])
      } catch (_e) {
        setError(t('error'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [show, t])

  useEffect(() => {
    if (!show) {
      setTitle('')
      setDocumentTypeId(null)
      setTemplateId(null)
      setCorrespondentId(null)
      setStoragePathId(null)
      setTags([])
      setCustomFieldValues({})
      setError(null)
      setCreating(false)
    }
  }, [show])

  const selectedType = useMemo(() => {
    if (!documentTypeId) return null
    if (selectedDocumentTypeDetails?.id === documentTypeId) return selectedDocumentTypeDetails
    return documentTypes.find((dt) => dt.id === documentTypeId) || null
  }, [documentTypeId, documentTypes, selectedDocumentTypeDetails])

  useEffect(() => {
    if (!documentTypeId) {
      setSelectedDocumentTypeDetails(null)
      return
    }

    let cancelled = false
    const loadDocumentTypeDetails = async () => {
      try {
        const details = await documentTypeService.getDocumentType(documentTypeId)
        if (!cancelled) setSelectedDocumentTypeDetails(details)
      } catch {
        // The list response remains a valid fallback when details are unavailable.
      }
    }
    void loadDocumentTypeDetails()

    return () => {
      cancelled = true
    }
  }, [documentTypeId])

  const selectedWordTemplate = useMemo(
    () => selectedType?.word_templates?.find((template) => template.id === templateId) || null,
    [selectedType, templateId],
  )

  const templateMappedFieldIds = useMemo(
    () => Object.values(selectedWordTemplate?.mappings || {}).map((fieldId) => Number(fieldId)),
    [selectedWordTemplate],
  )

  const selectedTypeCustomFields = useMemo(() => {
    if (!selectedType) return []
    const configuredFieldIds = selectedType.custom_field_configs && selectedType.custom_field_configs.length > 0
      ? selectedType.custom_field_configs.map((cfg) => cfg.custom_field)
      : selectedType.custom_fields || []
    // A cached list response can briefly omit the type configuration. Mapped
    // fields are still valid fields for this form and must remain visible.
    return Array.from(new Set([...configuredFieldIds, ...templateMappedFieldIds]))
  }, [selectedType, templateMappedFieldIds])

  const selectedTypeRequiredConfigs = useMemo(() => {
    if (!selectedType?.custom_field_configs) return []
    return selectedType.custom_field_configs.filter((cfg) => cfg.required)
  }, [selectedType])

  const customFieldById = useMemo(() => {
    const map = new Map<number, CustomField>()
    customFields.forEach((field) => map.set(field.id, field))
    return map
  }, [customFields])

  const effectiveCustomFieldModels = useMemo(() => {
    if (!selectedType) return []
    if (selectedType.custom_field_configs && selectedType.custom_field_configs.length > 0) {
      const configuredModels = [...selectedType.custom_field_configs]
        .sort((a, b) => a.order - b.order)
        .map((cfg) => {
          const baseField = customFieldById.get(cfg.custom_field)
          return {
            id: cfg.custom_field,
            name: cfg.override_name || cfg.custom_field_name || baseField?.name || `#${cfg.custom_field}`,
            data_type: cfg.custom_field_data_type || baseField?.data_type || 'string',
            placeholder: cfg.override_placeholder || cfg.custom_field_placeholder || baseField?.placeholder || '',
            default_value:
              cfg.override_default_value !== null && cfg.override_default_value !== undefined
                ? cfg.override_default_value
                : cfg.custom_field_default_value ?? baseField?.default_value ?? null,
            required: !!cfg.required,
            extra_data: baseField?.extra_data,
          }
        })
      const configuredIds = new Set(configuredModels.map((field) => field.id))
      const mappedFallbackModels = selectedTypeCustomFields
        .filter((fieldId) => !configuredIds.has(fieldId))
        .map((fieldId) => {
          const baseField = customFieldById.get(fieldId)
          return {
            id: fieldId,
            name: baseField?.name || `#${fieldId}`,
            data_type: baseField?.data_type || 'string',
            placeholder: baseField?.placeholder || '',
            default_value: baseField?.default_value ?? null,
            required: false,
            extra_data: baseField?.extra_data,
          }
        })
      return [...configuredModels, ...mappedFallbackModels]
    }
    return selectedTypeCustomFields.map((fieldId) => {
      const baseField = customFieldById.get(fieldId)
      return {
        id: fieldId,
        name: baseField?.name || `#${fieldId}`,
        data_type: baseField?.data_type || 'string',
        placeholder: baseField?.placeholder || '',
        default_value: baseField?.default_value ?? null,
        required: false,
        extra_data: baseField?.extra_data,
      }
    })
  }, [selectedType, selectedTypeCustomFields, customFieldById])

  useEffect(() => {
    setCustomFieldValues((prev) => {
      const filtered: Record<number, any> = {}
      const defaultsByField = new Map<number, any>()
      const counterFieldIds = new Set<number>()
      effectiveCustomFieldModels.forEach((field) => {
        defaultsByField.set(field.id, field.default_value)
        if (field.data_type === 'counter') {
          counterFieldIds.add(field.id)
        }
      })
      selectedTypeCustomFields.forEach((fieldId) => {
        if (counterFieldIds.has(fieldId)) {
          return
        }
        if (Object.prototype.hasOwnProperty.call(prev, fieldId)) {
          filtered[fieldId] = prev[fieldId]
        } else if (defaultsByField.has(fieldId) && defaultsByField.get(fieldId) !== null && defaultsByField.get(fieldId) !== undefined) {
          filtered[fieldId] = defaultsByField.get(fieldId)
        }
      })
      return filtered
    })
  }, [selectedTypeCustomFields, effectiveCustomFieldModels])

  const handleCustomFieldValueChange = (fieldId: number, value: any) => {
    setCustomFieldValues((prev) => ({ ...prev, [fieldId]: value }))
  }

  const showValidationError = (message: string) => {
    setError(message)
    toastService.showError(message)
  }

  const renderCustomFieldInput = (field: any) => {
    const fieldValue = customFieldValues[field.id]
    switch (field.data_type) {
      case 'date':
        return (
          <Form.Control
            type="date"
            value={fieldValue || ''}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value || null)}
          />
        )
      case 'integer':
        return (
          <Form.Control
            type="number"
            value={fieldValue ?? ''}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value === '' ? null : parseInt(e.target.value, 10))}
          />
        )
      case 'float':
        return (
          <Form.Control
            type="number"
            step="0.01"
            value={fieldValue ?? ''}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value === '' ? null : parseFloat(e.target.value))}
          />
        )
      case 'boolean':
        return (
          <Form.Check
            type="checkbox"
            checked={!!fieldValue}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.checked)}
          />
        )
      case 'select':
        return (
          <Form.Select value={fieldValue || ''} onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value || null)}>
            <option value="">{t('select_option')}</option>
            {(field.extra_data?.select_options || []).map((option: any) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        )
      case 'longtext':
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={fieldValue || ''}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value)}
          />
        )
      case 'documentlink':
        return (
          <DocumentLinkInput
            value={Array.isArray(fieldValue) ? fieldValue : []}
            onChange={(value) => handleCustomFieldValueChange(field.id, value)}
          />
        )
      case 'monetary':
        return (
          <MonetaryInput
            value={fieldValue || ''}
            onChange={(v) => handleCustomFieldValueChange(field.id, v)}
            defaultCurrency={field.extra_data?.default_currency || 'RSD'}
            size="sm"
          />
        )
      case 'url':
        return (
          <Form.Control
            type="url"
            value={fieldValue || ''}
            placeholder={field.placeholder || 'https://'}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value || null)}
          />
        )
      case 'counter':
        return (
          <div className="input-group">
            <Form.Control type="text" value="" readOnly disabled placeholder={t('counter_pending')} />
            <span className="input-group-text" title={t('counter_frozen_help')}>
              <i className="bi bi-lock"></i>
            </span>
          </div>
        )
      default:
        return (
          <Form.Control
            type="text"
            value={fieldValue ?? ''}
            placeholder={field.placeholder || ''}
            onChange={(e) => handleCustomFieldValueChange(field.id, e.target.value || null)}
          />
        )
    }
  }

  const handleCreate = async () => {
    if (!documentTypeId) {
      showValidationError(t('select_option'))
      return
    }
    if (!title.trim()) {
      showValidationError(t('title_required'))
      return
    }
    const counterFieldIds = new Set(
      effectiveCustomFieldModels.filter((field) => field.data_type === 'counter').map((field) => field.id),
    )
    const requiredFieldIds = new Set(selectedTypeRequiredConfigs.map((cfg) => cfg.custom_field))
    if (!selectedWordTemplate) {
      const missingRequired = effectiveCustomFieldModels.filter((field) => {
        if (counterFieldIds.has(field.id) || !requiredFieldIds.has(field.id)) {
          return false
        }
        const value = customFieldValues[field.id]
        if (value === null || value === undefined) return true
        if (typeof value === 'string' && value.trim() === '') return true
        if (Array.isArray(value) && value.length === 0) return true
        return false
      })
      if (missingRequired.length > 0) {
        showValidationError(
          `${t('missing_required_attributes')}: ${missingRequired.map((field) => field.name).join(', ')}`,
        )
        return
      }
    }
    const customFieldsPayload: Record<number, any> = {}
    Object.entries(customFieldValues).forEach(([fieldId, value]) => {
      const id = Number(fieldId)
      if (!counterFieldIds.has(id)) {
        customFieldsPayload[id] = value
      }
    })
    setCreating(true)
    setError(null)
    try {
      await onCreate({
        title: title.trim(),
        document_type: documentTypeId,
        correspondent: correspondentId,
        storage_path: storagePathId,
        tags,
        custom_fields: selectedWordTemplate ? undefined : customFieldsPayload,
        template_id: templateId,
      })
      onHide()
    } catch (e: any) {
      const backendError = e?.response?.data?.error || e?.response?.data?.detail
      setError(backendError || t('error'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered scrollable size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {t('create')} {t('document')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {t('document_type')}
                <span className="text-danger ms-1">*</span>
              </Form.Label>
              <Form.Select
                value={documentTypeId || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  setDocumentTypeId(Number.isNaN(val) ? null : val)
                  setSelectedDocumentTypeDetails(null)
                  setTemplateId(null)
                  setCustomFieldValues({})
                }}
              >
                <option value="">{t('select_option')}</option>
                {documentTypes.map((dt) => (
                  <option key={dt.id} value={dt.id}>
                    {dt.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {selectedType && (
              <Form.Group className="mb-3">
                <Form.Label>{t('word_template') || 'Word šablon'}</Form.Label>
                <Form.Select
                  value={templateId || ''}
                  onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">{t('without_template') || 'Bez šablona'}</option>
                  {(selectedType.word_templates || []).map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedWordTemplate && (
                  <Form.Text className="text-muted">
                    {t('template_attributes_after_creation') || 'Atribute možete popuniti nakon kreiranja dokumenta.'}
                  </Form.Text>
                )}
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                {t('title')}
                <span className="text-danger ms-1">*</span>
              </Form.Label>
              <Form.Control type="text" required value={title} onChange={(e) => setTitle(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('correspondent')}</Form.Label>
              <Form.Select
                value={correspondentId || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  setCorrespondentId(Number.isNaN(val) ? null : val)
                }}
              >
                <option value="">{t('not_assigned')}</option>
                {correspondents.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('storage_path')}</Form.Label>
              <Form.Select
                value={storagePathId || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  setStoragePathId(Number.isNaN(val) ? null : val)
                }}
              >
                <option value="">{t('default')}</option>
                {storagePaths.map((sp) => (
                  <option key={sp.id} value={sp.id}>
                    {sp.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('tags')}</Form.Label>
              <InputTags value={tags} onChange={setTags} hideAddButton={true} menuPortalTarget={document.body} />
            </Form.Group>

            {documentTypeId && !selectedWordTemplate && selectedTypeCustomFields.length > 0 && (
              <section className="create-document-attributes-section mb-3" aria-labelledby="create-document-attributes-title">
                <div id="create-document-attributes-title" className="create-document-attributes-title">
                  {t('custom_fields') || 'Atributi'}
                </div>
                <div className="create-document-attributes-fields">
                  {effectiveCustomFieldModels.map((field, index) => {
                    if (field.data_type === 'separator') {
                      const previousField = effectiveCustomFieldModels[index - 1]
                      const nextField = effectiveCustomFieldModels[index + 1]
                      const hasAttributeBefore = !!previousField && previousField.data_type !== 'separator'
                      const hasAttributeAfter = !!nextField && nextField.data_type !== 'separator'
                      if (!hasAttributeBefore || !hasAttributeAfter) return null
                      return <hr key={`sep-${field.id}`} className="create-document-attribute-separator" />
                    }
                    return (
                      <div key={field.id} className="create-document-attribute-field">
                        <Form.Label className="create-document-attribute-label">
                          {field.name}
                          {field.required && <span className="text-danger ms-1">*</span>}
                        </Form.Label>
                        {renderCustomFieldInput(field)}
                      </div>
                    )
                  })}
                </div>
              </section>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={creating}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={creating || loading}>
          {creating && <span className="spinner-border spinner-border-sm me-2" />}
          {t('create')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CreateDocumentDialog
